import { useState } from "react";
import { useUiStore } from "../stores/uiStore";
import { useAuthStore } from "../stores/authStore";
import css from "./SignIn.module.css";

/**
 * The terminal sign-in card. Under /dev the same component runs with
 * `onDevSignIn`, which installs a fake session instead of touching Firebase —
 * that is how the authed screens are reachable with no backend.
 */
export default function SignIn({
  workspaceHint = "acme-ai",
  onDevSignIn,
}: {
  workspaceHint?: string;
  onDevSignIn?: () => void;
}) {
  const scanlines = useUiStore((s) => s.scanlines);
  const signInEmail = useAuthStore((s) => s.signInEmail);
  const signUpEmail = useAuthStore((s) => s.signUpEmail);
  const signInGoogle = useAuthStore((s) => s.signInGoogle);

  // Empty, not a sample identity: a value here is submitted verbatim, so a
  // pre-filled demo address becomes a real failed login attempt for whoever
  // types only their password. The sample belongs in the placeholder.
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function attempt(fn: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  /** Both actions need the same fields; check once so neither round-trips a
   *  request that Firebase can only reject. */
  function missingCredentials(): boolean {
    if (!email.trim() || !password) {
      setError("email and passwd are both required");
      return true;
    }
    return false;
  }

  const authenticate = () => {
    if (onDevSignIn) {
      onDevSignIn();
      return;
    }
    if (missingCredentials()) return;
    void attempt(() => signInEmail(email.trim(), password));
  };

  // Creates the account. Previously this ran `authenticate`, so the one control
  // offering to make a new workspace could only ever sign in an existing user —
  // a brand-new user had no way through the console at all.
  const register = () => {
    if (onDevSignIn) {
      onDevSignIn();
      return;
    }
    if (missingCredentials()) return;
    void attempt(() => signUpEmail(email.trim(), password));
  };

  const google = () => {
    if (onDevSignIn) {
      onDevSignIn();
      return;
    }
    void attempt(() => signInGoogle());
  };

  return (
    <div className={css.root}>
      {scanlines ? <div className={css.scan} /> : null}
      <fieldset className={css.card}>
        <legend className={css.legend}>vectorforge</legend>
        <div className={css.tagline}>// stateless vector database · console v2.4</div>
        <div className={css.subline}>
          authenticate to workspace <span style={{ color: "var(--vf-acc)" }}>{workspaceHint}</span> via
          Firebase
        </div>

        <form
          className={css.fields}
          onSubmit={(e) => {
            e.preventDefault();
            authenticate();
          }}
        >
          <div className={css.field}>
            <label className={css.fieldLabel} htmlFor="vf-email">
              ▸ email
            </label>
            <span className={css.fieldColon}>:&nbsp;</span>
            <input
              id="vf-email"
              className={css.input}
              value={email}
              placeholder="you@example.com"
              autoComplete="username"
              onChange={(e) => setEmail(e.currentTarget.value)}
            />
          </div>
          <div className={css.field}>
            <label className={css.fieldLabel} htmlFor="vf-passwd">
              ▸ passwd
            </label>
            <span className={css.fieldColon}>:&nbsp;</span>
            <input
              id="vf-passwd"
              className={css.input}
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
            />
          </div>

          <div className={css.actions}>
            <button type="submit" className={css.primary} disabled={busy}>
              [ authenticate ]
            </button>
            <button type="button" className={css.secondary} onClick={google} disabled={busy}>
              [ google sso ]
            </button>
            <span className={css.beacon} aria-hidden="true" />
          </div>
        </form>

        {error ? <div className={css.error}>✗ {error}</div> : null}

        <p className={css.footnote}>
          no workspace yet?{" "}
          <button type="button" className={css.link} onClick={register} disabled={busy}>
            vf init --new
          </button>{" "}
          · a default workspace is auto-created on first login
        </p>
      </fieldset>
    </div>
  );
}
